import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  Text,
  Card,
  CardHeader,
  CardBody,
  Heading,
  List,
  ListItem,
  IconButton,
  HStack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { Plus } from '@phosphor-icons/react';
import { GoogleMap, MarkerF, useLoadScript } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from '../../config/googleMapsConfig';
import { useSublocations } from '../../hooks/useSublocations';
import SiteLocationsList from '../site/SiteLocationsList';

import { useSelector } from 'react-redux';
import { useAuth } from '../../hooks/useAuth';

export default function SublocationsManager({ siteId, site, onVisibilityChange }) {
  const user = useSelector((state) => state.auth.user);
  const { isAdmin } = useAuth();
  const canManage = useMemo(() => {
    return isAdmin || (user?.uid && user.uid === site?.submittedBy);
  }, [isAdmin, user, site?.submittedBy]);
  const {
    sublocations,
    loading,
    error,
    createSublocation,
    updateSublocation,
    deleteSublocation
  } = useSublocations(siteId);

  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [coordinates, setCoordinates] = useState(null);
  const initialRef = useRef();
  
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast({
        title: "Name required",
        description: "Please enter a name for the sublocation",
        status: "error",
        duration: 3000,
      });
      return;
    }

    try {
      if (!coordinates) {
        toast({
          title: "Location required",
          description: "Please select a location on the map",
          status: "error",
          duration: 3000,
        });
        return;
      }

      if (editingId) {
        await updateSublocation(editingId, {
          name: name.trim(),
          description: description.trim(),
          coordinates
        });
        toast({
          title: "Sublocation Updated",
          description: "The sublocation has been updated successfully",
          status: "success",
          duration: 3000,
        });
      } else {
        await createSublocation({
          name: name.trim(),
          description: description.trim(),
          coordinates
        });
        toast({
          title: "Sublocation Created",
          description: "New sublocation has been created successfully",
          status: "success",
          duration: 3000,
        });
      }
      handleClose();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleEdit = (sublocation) => {
    setEditingId(sublocation.id);
    setName(sublocation.name);
    setDescription(sublocation.description || '');
    setCoordinates(sublocation.coordinates || null);
    onOpen();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this sublocation?')) {
      try {
        await deleteSublocation(id);
        toast({
          title: "Sublocation Deleted",
          description: "The sublocation has been deleted successfully",
          status: "success",
          duration: 3000,
        });
      } catch (err) {
        toast({
          title: "Error",
          description: err.message,
          status: "error",
          duration: 3000,
        });
      }
    }
  };

  const handleClose = () => {
    setEditingId(null);
    setName('');
    setDescription('');
    setCoordinates(null);
    onClose();
  };

  const handleAddNew = () => {
    setEditingId(null);
    setName('');
    setDescription('');
    // Initialize coordinates with the main site location
    setCoordinates(site?.location?.coordinates || null);
    onOpen();
  };

  if (error) {
    return <Text color="red.500">Error loading sublocations: {error}</Text>;
  }

  return (
    <Card>
      <CardHeader>
        <HStack justify="space-between" align="center">
          <Heading size="sm">Site Locations</Heading>
          {canManage && (
            <Button
              size="sm"
              colorScheme="blue"
              onClick={handleAddNew}
              leftIcon={<Plus size={18} />}
            >
              Add Location
            </Button>
          )}
        </HStack>
      </CardHeader>
      <CardBody>
        <VStack spacing={4} align="stretch">
          {loading ? (
            <Text>Loading...</Text>
          ) : sublocations.length === 0 ? (
            <Text color="gray.500">No locations have been added yet</Text>
          ) : (
            <SiteLocationsList
              subLocations={sublocations}
              isOwner={canManage}
              onEditSubLocation={canManage ? handleEdit : undefined}
              onDeleteSubLocation={canManage ? handleDelete : undefined}
              onVisibilityChange={onVisibilityChange}
            />
          )}
        </VStack>
      </CardBody>

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        initialFocusRef={initialRef}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent as="form" onSubmit={handleSubmit}>
          <ModalHeader>
            {editingId ? "Edit Location" : "Add New Location"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
              <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input
                  ref={initialRef}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter location name"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter location description (optional)"
                />
              </FormControl>

              <Box height="300px" borderRadius="md" overflow="hidden" border="1px solid" borderColor="gray.200">
                {isLoaded && (
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={coordinates || (site?.location?.coordinates || { lat: 0, lng: 0 })}
                    zoom={coordinates ? 15 : (site?.location?.coordinates ? 15 : 2)}
                    onClick={(e) => {
                      const lat = e.latLng.lat();
                      const lng = e.latLng.lng();
                      setCoordinates({ lat, lng });
                    }}
                  >
                    {coordinates && (
                      <MarkerF
                        position={coordinates}
                        draggable
                        onDragEnd={(e) => {
                          const lat = e.latLng.lat();
                          const lng = e.latLng.lng();
                          setCoordinates({ lat, lng });
                        }}
                      />
                    )}
                  </GoogleMap>
                )}
              </Box>
              <Text fontSize="sm" color="gray.500" textAlign="center">
                Click on the map to set the location or drag the marker to adjust
              </Text>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button type="submit" colorScheme="blue" mr={3}>
              {editingId ? "Save Changes" : "Create Location"}
            </Button>
            <Button onClick={handleClose}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
}
